import { acceptOrgView, selectActiveOrganizationId } from './orgContext.mjs'

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const ACTIVE_ORG_STORAGE_KEY = 'ds-active-organization-id';
const ACTIVE_ORG_USER_KEY = 'ds-active-organization-user-id';

let orgViewGeneration = 0;

export function currentOrgView() {
  const user = readStoredUser();
  return {
    generation: orgViewGeneration,
    userId: user?.id ? String(user.id) : null,
    orgId: getActiveOrgId(),
  };
}

export function isCurrentOrgView(ticket: { generation: number; userId: string | null; orgId: string | null }) {
  return acceptOrgView(ticket, currentOrgView());
}

function isUUID(s: string): boolean {
  return typeof s === 'string' && UUID_REGEX.test(s);
}

export type UserOrgOption = { id: string; name: string };

function readStoredUser(): {
  id?: string;
  organizationIds?: string[];
  organizationsDetailed?: Array<{ id: string; name?: string }>;
  organizations?: Array<string | { id?: string; name?: string }>;
  organizationId?: string;
} | null {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** List organizations available to the current user (from auth payload). */
export function listUserOrganizations(): UserOrgOption[] {
  const user = readStoredUser();
  if (!user) return [];

  const detailed = Array.isArray(user.organizationsDetailed) ? user.organizationsDetailed : [];
  if (detailed.length > 0) {
    return detailed
      .filter((o) => o?.id && isUUID(String(o.id)))
      .map((o) => ({ id: String(o.id), name: o.name || String(o.id) }));
  }

  const ids = Array.isArray(user.organizationIds) ? user.organizationIds : [];
  const names = Array.isArray(user.organizations) ? user.organizations : [];
  const out: UserOrgOption[] = [];
  for (const entry of names) {
    if (!entry || typeof entry !== 'object' || !entry.id || !isUUID(String(entry.id))) continue;
    out.push({ id: String(entry.id), name: entry.name || String(entry.id) });
  }
  for (let i = 0; i < ids.length; i += 1) {
    const id = String(ids[i] || '');
    if (!isUUID(id)) continue;
    const nameEntry = names[i];
    const name =
      typeof nameEntry === 'string'
        ? nameEntry
        : nameEntry && typeof nameEntry === 'object' && nameEntry.name
          ? String(nameEntry.name)
          : id;
    if (out.some((item) => item.id === id)) continue;
    out.push({ id, name });
  }
  return out;
}

/**
 * Persist preferred organization for API scoping (multi-org users).
 * Dispatches `ds-active-organization-changed` so pages can reload.
 */
export function setActiveOrgId(organizationId: string | null): void {
  try {
    orgViewGeneration += 1;
    const userId = readStoredUser()?.id ? String(readStoredUser()?.id) : '';
    if (organizationId && isUUID(organizationId) && userId) {
      localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, organizationId);
      localStorage.setItem(ACTIVE_ORG_USER_KEY, userId);
    } else {
      localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
      localStorage.removeItem(ACTIVE_ORG_USER_KEY);
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('ds-active-organization-changed', { detail: { organizationId } }),
      );
    }
  } catch {
    // ignore storage errors
  }
}

/**
 * Get active organization ID (UUID) for API requests.
 * Prefer explicit preference, then organizationIds[0], then organizationsDetailed[0].id.
 */
export function getActiveOrgId(): string | null {
  try {
    const user = readStoredUser();
    const userId = user?.id ? String(user.id) : '';
    const storedOrgId = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
    const storedUserId = localStorage.getItem(ACTIVE_ORG_USER_KEY);
    const selected = selectActiveOrganizationId({
      userId,
      allowedIds: listUserOrganizations().map((org) => org.id),
      storedOrgId: storedOrgId && isUUID(storedOrgId) ? storedOrgId : null,
      storedUserId,
    });
    if (storedOrgId && storedUserId && storedUserId !== userId) {
      localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
      localStorage.removeItem(ACTIVE_ORG_USER_KEY);
    }
    return selected;
  } catch {
    return null;
  }
}

/** Append organization_id query param when orgId is a UUID. */
export function withOrganizationQuery(url: string, organizationId?: string | null): string {
  const orgId = organizationId || getActiveOrgId();
  if (!orgId || !isUUID(orgId)) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}organization_id=${encodeURIComponent(orgId)}`;
}
